import React, {useEffect, useMemo, useState} from 'react';
import {View} from 'react-native';
import {useStyles} from '../../../shared/hooks/styles';
import {Column} from '../../../shared/components/table/model';
import {Text} from '../../../shared/components/text';
import {getApiRoute} from '../../../shared/pipes';
import {Table as CommonTable} from '../../../shared/components/table';
import {useSelector} from 'react-redux';
import {getFormValues} from 'redux-form';
import {Forms} from '../../../shared/models/forms';
import {SearchBarForm} from '../SearchBar/model';
import {useIsDesktop} from '../../../shared/hooks/useIsDesktop';
import {HttpWrapperOptions, httpService} from '../../../shared/httpWrapper';
import {apiUrls} from '../../../shared/constants/urls';
import {MarketTransactionHistory} from '../../../shared/models/api/dobroruResponseData';
import {HistoryCardsList} from '../HistoryCards/HistoryCardsList/HistoryCardsList';

export const HistoryTable = () => {
  const styles = useStyles(({theme}) => ({
    root: {},
    title: {
      marginBottom: 30,
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'center',
    },
    empty: {
      alignItems: 'center',
    },
    emptyText: {
      color: theme.color.iconGrayColor,
    },
    th: {
      ...theme.fonts.body,
      color: theme.color.iconGrayColor,
    },
    td: {
      ...theme.fonts.h6Light,
    },
    colName: {
      flex: 1,
      paddingRight: 40,
    },
    colHours: {
      flexBasis: 160,
      flexShrink: 0,
      textAlign: 'right',
      paddingRight: 40,
    },
    colDate: {
      flexBasis: 190,
      flexShrink: 0,
    },
    loadMoreWrapper: {
      marginTop: 10,
      alignItems: 'center',
    },
    loadMoreButton: {
      borderWidth: 0,
      width: 180,
    },
  }));

  const columns: Column<MarketTransactionHistory>[] = useMemo(() => {
    return [
      {
        title: 'Наименование операции',
        field: 'operationName',
        headStyles: {colHeadStyle: styles.colName, colHeadTextStyle: styles.th},
        styles: {colBodyStyle: styles.colName, colBodyTextStyle: styles.td},
      },
      {
        title: 'Кол-во',
        field: 'quantity',
        headStyles: {colHeadStyle: styles.colHours, colHeadTextStyle: styles.th},
        styles: {colBodyStyle: styles.colHours, colBodyTextStyle: styles.td},
      },
      {
        title: 'Кол-во баллов',
        field: 'cost',
        headStyles: {colHeadStyle: styles.colHours, colHeadTextStyle: styles.th},
        styles: {colBodyStyle: styles.colHours, colBodyTextStyle: styles.td},
      },
      {
        title: 'Дата ',
        render: ({orderDateTime}) => {
          const orderDateTimeNew = new Date(orderDateTime);

          const formattedDate = orderDateTimeNew.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });

          const formattedTime = orderDateTimeNew.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
          });

          const formattedDateTime = `${formattedDate} ${formattedTime}`;

          return <Text type='h6Light'>{formattedDateTime.replace(/\//g, '.')}</Text>;
        },
        field: 'orderDateTime',
        headStyles: {colHeadStyle: styles.colDate, colHeadTextStyle: styles.th},
        styles: {colBodyStyle: styles.colDate},
      },
    ];
  }, [styles]);

  const isDesktop = useIsDesktop();
  const [visibleData, setVisibleData] = useState<MarketTransactionHistory[]>([]);
  const searchHistoryQuery = useSelector(getFormValues(Forms.searchBarForm)) as SearchBarForm;

  const allHistory = React.useRef([] as MarketTransactionHistory[]);
  const requestMarketTransactionsHistory = React.useCallback(async () => {
    const response = await httpService.get<MarketTransactionHistory[]>(
      getApiRoute(apiUrls.getMarketTransactionsHistory),
      new HttpWrapperOptions().Errors(false).ThrowException(true).TrackRequest(),
    );
    if (response) {
      allHistory.current = response;
      setVisibleData(response);
    }
  }, []);

  useEffect(() => {
    requestMarketTransactionsHistory();
  }, []);

  useEffect(() => {
    if (!searchHistoryQuery && allHistory.current.length) {
      setVisibleData(allHistory.current);
    }
    if (searchHistoryQuery?.query && allHistory.current.length) {
      const searchQuery = searchHistoryQuery.query.toLowerCase();
      setVisibleData(() => {
        const filteredVisibleHistoryItems = allHistory.current.filter((item) => {
          if (item.operationName?.toLowerCase().includes(searchQuery)) return item;
        });
        return filteredVisibleHistoryItems;
      });
    }
  }, [searchHistoryQuery]);

  return (
    <>
      {!visibleData || !visibleData.length ? (
        <View style={styles.empty}>
          <Text type='h5Light' style={styles.emptyText}>
            Список покупок пуст
          </Text>
        </View>
      ) : (
        <>
          {isDesktop ? (
            <CommonTable columns={columns} testIdPrefix='volunteers-event-row' data={visibleData} />
          ) : (
            <HistoryCardsList data={visibleData} />
          )}
        </>
      )}
    </>
  );
};
